import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { greetingsApi } from '../api/endpoints';
import { toast } from 'sonner';

const GreetingsCard = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [u, l] = await Promise.all([greetingsApi.upcoming(30), greetingsApi.log()]);
      setUpcoming(u);
      setLog(l);
    } catch (e) { console.error('Greetings load failed', e); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await greetingsApi.run();
      toast.success(`Greetings sent: ${r.sent} · Already sent today: ${r.skipped_already_sent}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to run greetings'); }
    finally { setRunning(false); }
  };

  const TypeBadge = ({ type }) => type === 'birthday'
    ? <Badge className="bg-pink-100 text-pink-700 border-pink-200 text-[10px]"><Icons.Cake className="w-3 h-3 mr-1" />Birthday</Badge>
    : <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]"><Icons.PartyPopper className="w-3 h-3 mr-1" />Anniversary</Badge>;

  return (
    <Card className="border-slate-200 p-4" data-testid="greetings-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <Icons.Gift className="w-4 h-4 text-brand-gold" /> Birthday &amp; Anniversary Greetings
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Auto-sends WhatsApp wishes daily at 9 AM for customer birthdays (from booking KYC) and booking anniversaries.
          </div>
        </div>
        <Button size="sm" variant="outline" disabled={running} onClick={runNow} data-testid="greetings-run-btn">
          {running ? <Icons.Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Icons.Send className="w-4 h-4 mr-1.5" />}
          Run Today's Greetings
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-slate-100 rounded-lg p-3" data-testid="greetings-upcoming">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Upcoming (30 days)</div>
          {upcoming.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">No customer birthdays or booking anniversaries in the next 30 days.</div>
          ) : upcoming.slice(0, 8).map((u, i) => (
            <div key={`${u.type}-${u.date}-${i}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 text-xs">
              <div className="min-w-0">
                <div className="font-medium truncate">{u.customer_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{u.project_name}{u.unit_no ? ` · Unit ${u.unit_no}` : ''}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <TypeBadge type={u.type} />
                <span className="text-[10px] text-slate-500">{u.in_days === 0 ? 'Today' : `in ${u.in_days}d`}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="border border-slate-100 rounded-lg p-3" data-testid="greetings-log">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Recently Sent</div>
          {log.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">No greetings sent yet.</div>
          ) : log.slice(0, 8).map(g => (
            <div key={g.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 text-xs">
              <div className="min-w-0">
                <div className="font-medium truncate">{g.customer_name}</div>
                <div className="text-[10px] text-slate-500">{g.date}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <TypeBadge type={g.greeting_type} />
                {g.mocked && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300">Mock</Badge>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default GreetingsCard;
